import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, ShoppingBag, CreditCard, Package } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { Link } from 'react-router-dom';

const enCartStorage = {
  getCart: () => {
    const cart = localStorage.getItem('cheat_cart_en');
    return cart ? JSON.parse(cart) : [];
  },
  saveCart: (cart) => {
    localStorage.setItem('cheat_cart_en', JSON.stringify(cart));
  },
  clearCart: () => {
    localStorage.removeItem('cheat_cart_en');
  },
  addItem: (item) => {
    const cart = enCartStorage.getCart();
    cart.push(item);
    enCartStorage.saveCart(cart);
  },
  removeItem: (index) => {
    const cart = enCartStorage.getCart();
    cart.splice(index, 1);
    enCartStorage.saveCart(cart);
  }
};

const CartEN = () => {
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuth();
  const [cart, setCart] = useState([]);
  const { toast } = useToast();

  useEffect(() => {
    setCart(enCartStorage.getCart());
  }, []);

  const removeItem = (index) => {
    const newCart = cart.filter((_, i) => i !== index);
    setCart(newCart);
    enCartStorage.saveCart(newCart);
    toast({
      title: 'Item removed',
      description: 'The item has been removed from your cart',
    });
  };

  const getTotal = () => {
    return cart.reduce((total, item) => total + item.price, 0);
  };

  const getTotalDiscount = () => {
    return cart.reduce((total, item) => {
      const orig = item.originalPrice || item.price;
      return total + (orig - item.price);
    }, 0);
  };

  const getOriginalTotal = () => {
    return cart.reduce((total, item) => total + (item.originalPrice || item.price), 0);
  };

  const handleDirectPayment = () => {
    const firstItem = cart[0];
    // EN products will have their own payment links
    // For now, show a message
    toast({
      title: 'Payment',
      description: 'Payment link coming soon for international products',
    });
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-black py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gray-900 rounded-2xl p-16 border-2 border-red-900/30">
            <ShoppingBag className="w-32 h-32 text-red-600/30 mx-auto mb-8" />
            <h1 className="text-4xl font-bold text-white mb-4 gaming-title">YOUR CART IS EMPTY</h1>
            <p className="text-gray-400 text-lg mb-8">
              Add awesome products to your cart to start dominating
            </p>
            <Link to="/en/products">
              <Button className="bg-red-600 hover:bg-red-700 text-white font-bold px-10 py-6 text-lg border-2 border-red-500 shadow-lg gaming-title">
                <Package className="w-5 h-5 mr-2" />
                VIEW PRODUCTS
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-16 px-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-12 gaming-title glow-red text-center md:text-left">
          SHOPPING CART
        </h1>
        
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item, index) => (
              <Card key={index} className="bg-gray-900 border-2 border-red-900/30 hover:border-red-600/50 transition-all card-premium">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-bold text-xl text-white gaming-title mb-2">{item.productName}</h3>
                      <div className="space-y-1">
                        <p className="text-gray-400">
                          <span className="text-red-600 font-semibold">Plan:</span> {item.planType}
                        </p>
                        <p className="text-gray-400">
                          <span className="text-red-600 font-semibold">Validity:</span> {item.validity}
                        </p>
                      </div>
                    </div>
                    <div className="text-right ml-6">
                      {item.originalPrice && item.originalPrice > item.price && (
                        <p className="text-gray-500 text-sm line-through">$ {item.originalPrice.toFixed(2)}</p>
                      )}
                      <p className="text-3xl font-bold text-red-600 gaming-title mb-1">$ {item.price.toFixed(2)}</p>
                      {item.originalPrice && item.originalPrice > item.price && (
                        <p className="text-green-400 text-xs font-semibold">Save $ {(item.originalPrice - item.price).toFixed(2)}</p>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(index)}
                        className="text-gray-400 hover:text-red-600 hover:bg-red-600/10 transition-all mt-2"
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div>
            <Card className="bg-gray-900 border-2 border-red-900/30 sticky top-6">
              <CardHeader className="border-b border-red-900/30">
                <CardTitle className="text-white gaming-title text-xl">ORDER SUMMARY</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="space-y-4">
                  <div className="flex justify-between text-gray-400">
                    <span>Subtotal</span>
                    <span className="font-semibold">$ {getOriginalTotal().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>Discount</span>
                    <span className="font-semibold text-green-500">- $ {getTotalDiscount().toFixed(2)}</span>
                  </div>
                  <div className="border-t border-red-900/30 pt-4">
                    <div className="flex justify-between">
                      <span className="font-bold text-lg text-white gaming-title">TOTAL</span>
                      <span className="text-3xl font-bold text-red-600 gaming-title">$ {getTotal().toFixed(2)}</span>
                    </div>
                  </div>
                  {getTotalDiscount() > 0 && (
                    <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-3 text-center">
                      <p className="text-green-400 text-sm font-bold">You're saving $ {getTotalDiscount().toFixed(2)}!</p>
                    </div>
                  )}
                </div>
                
                <Button
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-6 text-lg border-2 border-red-500 shadow-lg hover:shadow-red-600/50 transition-all gaming-title"
                  onClick={handleDirectPayment}
                >
                  <CreditCard className="w-5 h-5 mr-2" />
                  CHECKOUT
                </Button>
                
                <div className="bg-gray-800 rounded-lg p-4 border border-red-900/20">
                  <p className="text-xs text-gray-400 text-center leading-relaxed">
                    Secure payment<br />
                    Instant delivery after confirmation
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartEN;
